import { AlertCircle, AlertTriangle, ChevronDown, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { deleteAccount, type AccountDeleteReason } from '@/features/auth/api';
import { clearUnreadCountCache } from '@/hooks/useUnreadNotificationCount';
import { clearEntitlementCache } from '@/hooks/useEntitlement';
import { clearSubscriptionStateCache } from '@/hooks/useSubscriptionState';
import { getAccountReturnTarget } from '@/lib/account-return-target';
import { ApiError, NetworkError } from '@/lib/api-client';
import { hardNavigate } from '@/lib/hard-navigate';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type SubmitErrorVariant = 'network' | 'delete' | null;

/**
 * A-15 Account-Delete-Page (Batch 9C — Briefing line 555-581).
 *
 * Multiple layered protections per Briefing intent ("mit mehreren
 * Schutzstufen gegen versehentliche Löschung"):
 *   1. Warning banner explaining cascade behaviour
 *   2. Error banner emphasising irreversibility
 *   3. Type-to-confirm input requiring exact "KONTO LÖSCHEN"
 *   4. Submit button disabled until input matches exactly (case-
 *      sensitive)
 *
 * Two exit paths intentional (Verification 2, Batch 9C Gate-1
 * Code-Review): the small back-link top-of-page covers the
 * "browsing, took a wrong turn" navigational case (~12px secondary
 * tone); the form-context outline Cancel button at the bottom
 * covers the "in the form, changed my mind" safety-exit case
 * (12-height, paired with the destructive Submit). Different visual
 * weights AND different positions — no redundancy. Dropping either
 * weakens the protection model on a destructive surface.
 *
 * Optional reason dropdown + feedback textarea collected per
 * Briefing but DISCARDED server-side at Mai-Launch per Flag 2
 * (analytics persistence deferred to tracker AccountDeletionReason
 * — Backend/Offen #23).
 *
 * Post-delete: clears all auth + cached-state + navigates to
 * /auth/login?accountDeleted=1. LoginRoute reads the query param
 * on mount and renders a neutral info-banner.
 */
export function DeleteRoute() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const user = useAuthStore((s) => s.user);
  // Sebastian ruling 2026-09-15 round 4 — return path + warning-items
  // both depend on which worlds the customer has. Return: book-only
  // → /book/meine; audio + both → /account. Warning items: always +
  // (audio when hasAudioWorld) + (book item when hasBookWorld).
  const returnTarget = getAccountReturnTarget(user);
  const hasAudioWorld = user?.hasAudioWorld ?? false;
  const hasBookWorld = user?.hasBookWorld ?? false;

  const [reason, setReason] = useState<AccountDeleteReason | ''>('');
  const [feedback, setFeedback] = useState('');
  const [confirmInput, setConfirmInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitErrorVariant>(null);

  const S = STRINGS.account.delete;
  const warningItems: readonly string[] = [
    ...S.warningItems.always,
    ...(hasAudioWorld ? S.warningItems.audio : []),
    ...(hasBookWorld ? [S.warningItems.book] : []),
  ];

  const isConfirmed = confirmInput === S.confirmInputRequired;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isConfirmed || isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload: { reason?: AccountDeleteReason; feedback?: string } = {};
      if (reason) payload.reason = reason;
      const trimmedFeedback = feedback.trim();
      if (trimmedFeedback) payload.feedback = trimmedFeedback;
      await deleteAccount(payload);
      // F10 (Sebastian field finding 2026-10-17, repro of the
      // 2026-10-16 fix that still failed in prod) — HARD navigate
      // after clearing state. The earlier "navigate first then
      // clearAuth" fix was PASSING in jsdom but FAILING in the real
      // browser (test used a stand-in ProtectedRoute, lesson
      // learned). The root race in prod:
      //   1. authStore uses `useSyncExternalStore`; clearAuth
      //      fires its listener synchronously.
      //   2. React Router's history also fires its listener
      //      synchronously on `navigate(…)`.
      //   3. React 18 batches both into a single re-render.
      //   4. In that render, ProtectedRoute at
      //      pwa/src/components/ProtectedRoute.tsx:8-10 could still
      //      see `location.pathname === '/account/delete'` AND
      //      `accessToken === null` depending on which listener's
      //      snapshot read landed first inside the batch —
      //      ProtectedRoute's `<Navigate>` then fired with
      //      `?returnTo=%2Faccount%2Fdelete` and OVERWROTE the
      //      imperative navigate on commit.
      // Hard reload fix: `window.location.replace(…)` synchronously
      // changes the browser URL AND cancels the current React
      // render tree. No further React updates process; no
      // ProtectedRoute re-render can race. The new URL is loaded
      // fresh from scratch — authStore reads an empty persist
      // slice (we clearAuth'd into localStorage) + LoginRoute
      // reads `?accountDeleted=1` from the fresh URL.
      //
      // Clear in-memory + persist state BEFORE the hard nav so the
      // fresh page load has nothing to resurrect.
      clearAuth();
      clearSubscriptionStateCache();
      clearUnreadCountCache();
      clearEntitlementCache();
      hardNavigate('/auth/login?accountDeleted=1');
      return;
    } catch (err) {
      setIsSubmitting(false);
      if (err instanceof NetworkError) {
        setSubmitError('network');
      } else if (err instanceof ApiError) {
        setSubmitError('delete');
      } else {
        setSubmitError('delete');
      }
    }
  };

  const submitErrorMessage =
    submitError === 'network'
      ? S.errors.networkError
      : submitError === 'delete'
        ? S.errors.deleteFailed
        : null;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="narrow">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate(returnTarget.path)}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {returnTarget.label}
          </button>

          <h1 className="text-[22px] font-semibold text-dream-navy mb-1">{S.title}</h1>
          <p className="text-sm text-dream-navy/70 mb-6">{S.subtitle}</p>

          {/* Warning-Banner (yellow/orange) — cascade explanation */}
          <div className="mb-4 p-4 rounded-md bg-dream-gold/15 border border-dream-gold/30 text-warning-text text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-2">{S.warningHeading}</p>
                <ul className="list-disc list-inside space-y-1">
                  {warningItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Error-Banner (red) — irreversibility */}
          <div className="mb-6 p-4 rounded-md bg-error/10 border border-error/30 text-error-text text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p className="font-semibold">{S.errorHeading}</p>
            </div>
          </div>

          {/* Submit-failure banner (separate from the irreversibility
              banner above, which always renders) */}
          {submitErrorMessage && (
            <div
              role="alert"
              data-testid="delete-submit-error"
              className="mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm flex items-start gap-2"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{submitErrorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Reason dropdown (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="delete-reason" className="text-sm text-dream-navy">
                {S.reasonLabel}
              </Label>
              <div className="relative">
                <select
                  id="delete-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value as AccountDeleteReason | '')}
                  className="h-11 w-full rounded-lg bg-dream-white text-dream-navy px-2.5 pr-9 appearance-none border border-input focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
                >
                  <option value="">{S.reasonPlaceholder}</option>
                  <option value="too_little_use">{S.reasonOptions.too_little_use}</option>
                  <option value="too_expensive">{S.reasonOptions.too_expensive}</option>
                  <option value="alternatives_better">{S.reasonOptions.alternatives_better}</option>
                  <option value="privacy_concerns">{S.reasonOptions.privacy_concerns}</option>
                  <option value="other">{S.reasonOptions.other}</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-dream-navy pointer-events-none" />
              </div>
            </div>

            {/* Feedback textarea (optional, max 500) */}
            <div className="space-y-1.5">
              <Label htmlFor="delete-feedback" className="text-sm text-dream-navy">
                {S.feedbackLabel}
              </Label>
              <textarea
                id="delete-feedback"
                rows={3}
                value={feedback}
                maxLength={500}
                placeholder={S.feedbackPlaceholder}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full rounded-lg bg-dream-white text-dream-navy px-2.5 py-2 border border-input focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none text-base md:text-sm resize-none"
              />
            </div>

            {/* Type-to-confirm */}
            <div className="space-y-1.5">
              <Label htmlFor="delete-confirm" className="text-sm text-dream-navy">
                {S.confirmInputLabel}
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                className="h-11 bg-dream-white text-dream-navy"
                data-testid="delete-confirm-input"
              />
            </div>

            <div className="flex flex-col gap-2 pt-2 sm:flex-row-reverse">
              <Button
                type="submit"
                variant="destructive"
                disabled={!isConfirmed || isSubmitting}
                className="h-12 text-base font-semibold flex-1"
                data-testid="delete-submit-button"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? S.submitLoading : S.submitButton}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(returnTarget.path)}
                disabled={isSubmitting}
                className="h-12 text-base font-semibold flex-1"
              >
                {S.cancelButton}
              </Button>
            </div>
          </form>
        </div>
      </ContentContainer>
    </div>
  );
}
